import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { ArrowLeft, PlusCircle, Search, Loader2 } from 'lucide-react';
import { usePortfolioStore } from '../../../state/portfolioStore';
import { searchCompanies } from '../../../data/stockScreenerService';

export default function AddHoldingPage() {
  const navigate = useNavigate();
  const addHolding = usePortfolioStore((s) => s.addHolding);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [symbol, setSymbol] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [exchange, setExchange] = useState('NSE');
  const [sector, setSector] = useState('');
  const [quantity, setQuantity] = useState('');
  const [avgBuyPrice, setAvgBuyPrice] = useState('');
  const [firstBuyDate, setFirstBuyDate] = useState(new Date().toISOString().split('T')[0]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    const results = await searchCompanies({ searchQuery: searchQuery.trim() });
    setSearchResults(results.slice(0, 10));
  };

  const selectCompany = (company) => {
    setSymbol(company.symbol);
    setCompanyName(company.name);
    setExchange(company.exchange || 'NSE');
    setSector(company.sector || '');
    setSearchResults([]);
    setSearchQuery('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!symbol || !quantity || !avgBuyPrice) {
      setError('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    await addHolding({
      symbol,
      companyName: companyName || symbol,
      exchange,
      sector,
      quantity: parseFloat(quantity),
      avgBuyPrice: parseFloat(avgBuyPrice),
      firstBuyDate,
      source: 'manual',
    });
    setSubmitting(false);
    setSuccess(true);
    setTimeout(() => navigate('/public/portfolio'), 1500);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <h1 className="text-2xl font-bold">Add Holding</h1>
      </div>

      {success && (
        <Alert className="mb-4 border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950">
          <AlertDescription className="text-emerald-700 dark:text-emerald-300">
            Holding added successfully! Redirecting to dashboard...
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Stock Details</CardTitle>
          <CardDescription>Search for a company or enter details manually</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-6">
            <Label className="mb-2 block">Search Company</Label>
            <div className="flex gap-2">
              <Input
                placeholder="Search by name or symbol..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button onClick={handleSearch} variant="outline"><Search className="w-4 h-4" /></Button>
            </div>
            {searchResults.length > 0 && (
              <div className="mt-2 border rounded-lg max-h-48 overflow-y-auto">
                {searchResults.map((company) => (
                  <button
                    key={company.id}
                    onClick={() => selectCompany(company)}
                    className="w-full text-left px-3 py-2 hover:bg-secondary border-b last:border-b-0 text-sm"
                  >
                    <span className="font-medium">{company.name}</span>
                    <span className="text-muted-foreground ml-2">{company.symbol}</span>
                    {company.sector && <span className="text-muted-foreground ml-2 text-xs">({company.sector})</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950">
                <AlertDescription className="text-red-700 dark:text-red-300">{error}</AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Symbol *</Label>
                <Input value={symbol} onChange={(e) => setSymbol(e.target.value.toUpperCase())} placeholder="e.g., TCS" required />
              </div>
              <div className="space-y-2">
                <Label>Company Name</Label>
                <Input value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="e.g., Tata Consultancy" />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Exchange</Label>
                <Select value={exchange} onValueChange={setExchange}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NSE">NSE</SelectItem>
                    <SelectItem value="BSE">BSE</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Quantity *</Label>
                <Input type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="0" min="0" step="1" required />
              </div>
              <div className="space-y-2">
                <Label>Avg. Buy Price *</Label>
                <Input type="number" value={avgBuyPrice} onChange={(e) => setAvgBuyPrice(e.target.value)} placeholder="0.00" min="0" step="0.01" required />
              </div>
            </div>

            <div className="space-y-2">
              <Label>First Buy Date</Label>
              <Input type="date" value={firstBuyDate} onChange={(e) => setFirstBuyDate(e.target.value)} />
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 gap-2 flex-1" disabled={submitting}>
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlusCircle className="w-4 h-4" />}
                Add Holding
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
